import React, { useState, useEffect } from 'react';
import ProfileImage from './ProfileImage';
import { Heart, Bookmark, MessageCircle } from 'lucide-react';

const TwitterIcon = ({ size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M23.954 4.569c-.885.392-1.83.656-2.825.775 1.014-.608 1.794-1.569 2.163-2.724-.951.564-2.005.974-3.127 1.195-.897-.959-2.178-1.559-3.594-1.559-2.717 0-4.92 2.203-4.92 4.917 0 .39.045.765.127 1.124-4.09-.205-7.719-2.165-10.148-5.144-.424.729-.666 1.576-.666 2.476 0 1.71.87 3.213 2.188 4.096-.807-.026-1.566-.248-2.23-.616v.062c0 2.385 1.693 4.374 3.946 4.827-.413.112-.849.171-1.296.171-.317 0-.626-.03-.927-.086.631 1.953 2.445 3.376 4.604 3.416-1.68 1.319-3.809 2.107-6.115 2.107-.398 0-.79-.023-1.175-.069 2.179 1.397 4.768 2.212 7.548 2.212 9.051 0 14-7.496 14-13.986 0-.21 0-.423-.015-.634.961-.695 1.8-1.562 2.46-2.549z" />
  </svg>
);

const RedditIcon = ({ size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm0 2c5.514 0 10 4.486 10 10s-4.486 10-10 10-10-4.486-10-10 4.486-10 10-10zm-4.356 12.956c.815.744 1.882 1.044 2.956 1.044s2.141-.3 2.956-1.044c.232-.212.597-.187.82.055.212.232.187.597-.055.82-.913.833-2.175 1.169-3.721 1.169s-2.809-.336-3.721-1.169c-.242-.223-.268-.588-.055-.82.223-.242.588-.267.82-.055zm.291-3.484c-.6 0-1.091.496-1.091 1.106s.486 1.106 1.091 1.106c.604 0 1.091-.496 1.091-1.106s-.491-1.106-1.091-1.106zm4.864 1.106c0-.61.486-1.106 1.091-1.106.605 0 1.091.496 1.091 1.106s-.491 1.106-1.091 1.106c-.604 0-1.091-.496-1.091-1.106z" />
  </svg>
);

/**
 * Search Suggestions Component
 * Shows suggestions when typing search operators like from:, (is:), (subreddit:)
 */
const SearchSuggestions = ({ 
  visible,
  operator, 
  query, 
  suggestions, 
  onSelect, 
  onKeyDown 
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  
  // Reset active index when suggestions change
  useEffect(() => {
    setActiveIndex(0);
  }, [suggestions, query, operator]);

  // Handle keyboard events (up/down/enter/escape)
  const handleKeyDown = (event) => {
    if (!visible || suggestions.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((prevIndex) => (prevIndex + 1) % suggestions.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((prevIndex) => 
          (prevIndex - 1 + suggestions.length) % suggestions.length
        );
        break;
      case 'Enter':
        event.preventDefault();
        if (suggestions[activeIndex]) {
          onSelect(suggestions[activeIndex]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        if (onKeyDown) onKeyDown(event);
        break;
      default:
        if (onKeyDown) onKeyDown(event);
    }
  };

  // Register and unregister keyboard event listeners
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [visible, suggestions, activeIndex]);

  // Get icon for type suggestion
  const getTypeIcon = (suggestion) => {
    switch (suggestion.icon) {
      case 'heart':
        return <Heart size={16} className="text-red-500" />;
      case 'bookmark':
        return <Bookmark size={16} className="text-blue-500" />;
      case 'twitter':
        return <TwitterIcon size={16} color="#1DA1F2" />;
      case 'reddit':
        return <RedditIcon size={16} color="#FF4500" />;
      default:
        return null;
    }
  };

  // If no suggestions or not visible, don't render anything
  if (!visible || suggestions.length === 0) return null;

  return (
    <div className="absolute z-10 mt-1 w-full max-w-[440px] bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 max-h-[300px] overflow-y-auto animate-fadeIn">
      <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
        {operator === 'from' && 'Suggested users'}
        {operator === 'subreddit' && 'Suggested subreddits'}
        {operator === 'is' && 'Content types'}
      </div>
      <ul className="py-1">
        {suggestions.map((suggestion, index) => (
          <li
            key={`${operator}-${suggestion.value}`}
            onClick={() => onSelect(suggestion)}
            onMouseEnter={() => setActiveIndex(index)}
            className={`px-4 py-2 cursor-pointer flex items-center hover:bg-gray-100 dark:hover:bg-gray-700 ${
              activeIndex === index ? 'bg-gray-100 dark:bg-gray-700' : ''
            }`}
          >
            <div className="flex-shrink-0 w-8 h-8 rounded-full overflow-hidden mr-2">
              {operator === 'from' && suggestion.image ? (
                <ProfileImage 
                  src={suggestion.image}
                  alt={suggestion.title}
                  userHandle={suggestion.value}
                  className="w-8 h-8"
                />
              ) : operator === 'is' ? (
                <div className="w-8 h-8 bg-gray-200 dark:bg-gray-600 flex items-center justify-center rounded-full">
                  {getTypeIcon(suggestion)}
                </div>
              ) : operator === 'subreddit' ? (
                <div className="w-8 h-8 bg-gray-200 dark:bg-gray-600 flex items-center justify-center rounded-full">
                  <RedditIcon size={16} color="#FF4500" />
                </div>
              ) : (
                <div className="w-8 h-8 bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-300 rounded-full">
                  {suggestion.title.charAt(0)}
                </div>
              )}
            </div>
            <div>
              <div className="font-medium text-gray-900 dark:text-white">
                {suggestion.displayName}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {suggestion.title}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default SearchSuggestions;